import type {
  ApiContext,
  ApiNotAuthorized,
  CardStateRefused,
  InvalidFormField,
  MarketNotLinked,
  MarketPrice,
  MarketRefresh,
  NewSealedProduct,
  SealedProduct,
  SealedProductListFilter,
  SealedProductNotFound,
  SealedProductPatch,
  SealedStockUpdate,
  StockLevel,
  StoreNotFound,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import { type AsyncResult, mapValue } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { BackendApi } from "./api";

type Api = { client: BackendApi; authHandler: AuthHandler<ApiContext> };

export function loadSealedProducts(
  o: Api & { shopId: string; filter?: SealedProductListFilter; search?: string },
): AsyncResult<SealedProduct[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) => o.client.sealed.list(ctx, o.shopId, { filter: o.filter, search: o.search }));
}

/** The hand-made items a scan can be linked to; all of them when the search finds none. */
export async function searchLinkableSealed(
  o: Api & { shopId: string; search: string },
): AsyncResult<{ items: SealedProduct[]; matched: boolean }, ApiNotAuthorized | StoreNotFound> {
  const search = o.search.trim();
  const found = await loadSealedProducts({ ...o, filter: "linkable", search: search || undefined });
  if (!found.ok || found.value.length > 0 || !search) return mapValue(found, (items) => ({ items, matched: true }));
  return mapValue(await loadSealedProducts({ ...o, filter: "linkable", search: undefined }), (items) => ({
    items,
    matched: false,
  }));
}

export function createSealedProduct(
  o: Api & { shopId: string; product: NewSealedProduct },
): AsyncResult<SealedProduct, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.sealed.create(ctx, o.shopId, o.product));
}

export function updateSealedProduct(
  o: Api & { shopId: string; sealedProductId: string; patch: SealedProductPatch },
): AsyncResult<
  SealedProduct,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.sealed.update(ctx, o.shopId, o.sealedProductId, o.patch));
}

export function setSealedStock(
  o: Api & { shopId: string; sealedProductId: string; change: SealedStockUpdate },
): AsyncResult<
  StockLevel,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | CardStateRefused | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.sealed.setStock(ctx, o.shopId, o.sealedProductId, o.change));
}

export function archiveSealedProduct(
  o: Api & { shopId: string; sealedProductId: string },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | CardStateRefused> {
  return o.authHandler.callApi((ctx) => o.client.sealed.archive(ctx, o.shopId, o.sealedProductId));
}

/** The shared market estimate and its history; the same in every shop holding the product. */
export function loadSealedMarketPrice(
  o: Api & { shopId: string; sealedProductId: string },
): AsyncResult<MarketPrice, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound> {
  return o.authHandler.callApi((ctx) => o.client.sealed.marketPrice(ctx, o.shopId, o.sealedProductId));
}

/** Asks the source for the market estimate, past the price cache. The shop's own prices stay. */
export function refreshSealedMarketPrice(
  o: Api & { shopId: string; sealedProductId: string },
): AsyncResult<
  MarketRefresh,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | MarketNotLinked
> {
  return o.authHandler.callApi((ctx) => o.client.sealed.refreshMarketPrice(ctx, o.shopId, o.sealedProductId));
}
